import React, { useState, useEffect } from 'react';
import AvatarSVG from "../components/AvatarSVG";
import { useNavigate } from 'react-router-dom';
import {
  Droplets,
  LogOut,
  MessageSquare,
  Users,
  Settings,
  Bell,
  Menu,
  X,
  Headphones,
  FileText,
  Briefcase,
  Palette,
  Phone,
  PhoneOff,
  Crown,
  BookOpen,
  Building2,
  MapPin,
  Database,
  AlertTriangle,
  FolderOpen,
  ChevronDown,
  ChevronUp,
  Sliders,
  Ticket,
  FolderKanban,
  Package,
  UserCircle,
  Send,
  Maximize2,
  Minimize2,
  Calendar,
  Camera,
  Save,
  Server,
  HardDrive,
  Paperclip,
  Image,
  FileIcon,
  Clock,
  Coffee,
  Play,
  Pause,
  CheckCircle2,
  Lock,
  Shield,
  Mail,
  Key,
  Link2,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  ChevronLeft
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api, { getDepartments, chatWithAI, getFichadorStatus, fichadorClockIn, fichadorClockOut, fichadorBreakStart, fichadorBreakEnd, getNotifications } from '../services/api';
import { connectSocket, onNewNotification, offNewNotification } from '../services/socket';
import Chat from '../components/Chat';
import UserManagement from '../components/UserManagement';
import AIChat from '../components/AIChat';
import SettingsPanel from '../components/SettingsPanel';
import AdminPanel from '../components/AdminPanel';
import NotificationsDropdown from '../components/NotificationsDropdown';
import KnowledgeManager from '../components/KnowledgeManager';
import FranquiciadosManager from '../components/FranquiciadosManager';
import AparatosManager from '../components/AparatosManager';
import TicketsManager from '../components/TicketsManager';
import FileManager from '../components/FileManager';
import ScreensManager from '../components/ScreensManager';
import GerenciaPanel from '../components/panels/GerenciaPanel';
import AdministracionPanel from '../components/panels/AdministracionPanel';
import ComercialPanel from '../components/panels/ComercialPanel';
import SATPanel from '../components/panels/SATPanel';
import ProyectosPanel from '../components/panels/ProyectosPanel';
import AlmacenPanel from '../components/panels/AlmacenPanel';
import FranquiciasPanel from '../components/panels/FranquiciasPanel';
import ClientesPanel from '../components/panels/ClientesPanel';
import AgendaPanel from '../components/panels/AgendaPanel';
import ServerPanel from '../components/panels/ServerPanel';
import ContactosPanel from '../components/panels/ContactosPanel';

const departmentIcons = {
  'Gerencia': Crown,
  'Soporte Tecnico': Headphones,
  'Administracion': FileText,
  'Comercial': Briefcase,
  'Diseno': Palette,
  'Equipo Entradas': Phone
};

// Colores distintivos para cada departamento
const departmentColors = {
  'Gerencia': { bg: 'bg-red-500', light: 'bg-red-50', border: 'border-red-500', text: 'text-red-600', hex: '#ef4444' },
  'Soporte Tecnico': { bg: 'bg-orange-500', light: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-600', hex: '#f97316' },
  'SAT': { bg: 'bg-orange-500', light: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-600', hex: '#f97316' },
  'Administracion': { bg: 'bg-blue-500', light: 'bg-blue-50', border: 'border-blue-500', text: 'text-blue-600', hex: '#3b82f6' },
  'Comercial': { bg: 'bg-green-500', light: 'bg-green-50', border: 'border-green-500', text: 'text-green-600', hex: '#22c55e' },
  'Diseno': { bg: 'bg-purple-500', light: 'bg-purple-50', border: 'border-purple-500', text: 'text-purple-600', hex: '#a855f7' },
  'Produccion': { bg: 'bg-amber-500', light: 'bg-amber-50', border: 'border-amber-500', text: 'text-amber-600', hex: '#f59e0b' },
  'Almacen': { bg: 'bg-cyan-500', light: 'bg-cyan-50', border: 'border-cyan-500', text: 'text-cyan-600', hex: '#06b6d4' },
  'Proyectos': { bg: 'bg-indigo-500', light: 'bg-indigo-50', border: 'border-indigo-500', text: 'text-indigo-600', hex: '#6366f1' },
  'Franquicias': { bg: 'bg-pink-500', light: 'bg-pink-50', border: 'border-pink-500', text: 'text-pink-600', hex: '#ec4899' },
  'Clientes': { bg: 'bg-teal-500', light: 'bg-teal-50', border: 'border-teal-500', text: 'text-teal-600', hex: '#14b8a6' },
  'RRHH': { bg: 'bg-yellow-500', light: 'bg-yellow-50', border: 'border-yellow-500', text: 'text-yellow-600', hex: '#eab308' },
  'Marketing': { bg: 'bg-rose-500', light: 'bg-rose-50', border: 'border-rose-500', text: 'text-rose-600', hex: '#f43f5e' },
  'default': { bg: 'bg-gray-500', light: 'bg-gray-50', border: 'border-gray-500', text: 'text-gray-600', hex: '#6b7280' }
};

// Función para obtener color del departamento
const getDeptColor = (deptName) => {
  return departmentColors[deptName] || departmentColors['default'];
};

// Etiquetas legibles para los roles
const roleLabels = {
  'superadmin': 'Super Admin',
  'admin': 'Administrador',
  'revisor': 'Revisor',
  'usuario': 'Usuario',
  'franquiciado': 'Franquiciado',
  'tecnico_externo': 'Tecnico Externo',
  'cliente_final': 'Cliente',
  'cuenta_cliente': 'Cuenta Cliente'
};

export default function Dashboard() {
  const { user, logout, hasDepartmentAccess, updateUserData } = useAuth();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('agenda'); // 'agenda', 'chat', 'users', 'ai', 'knowledge', 'aparatos', 'franquiciados', 'tickets', 'files', 'screens'
  const [showSettings, setShowSettings] = useState(false);
  const [accountData, setAccountData] = useState({ name: '', email: '' });
  const [savingAccount, setSavingAccount] = useState(false);
  const [accountMessage, setAccountMessage] = useState('');
  // Inicializar datos de Mi Cuenta cuando hay usuario
  useEffect(() => {
    if (user) {
      setAccountData({ name: user.name || '', email: user.email || '' });
    }
  }, [user]);

  // Guardar datos de Mi Cuenta
  const handleSaveAccount = async () => {
    setSavingAccount(true);
    setAccountMessage('');
    try {
      await api.put(`/api/users/${user.id}`, {
        name: accountData.name,
        email: accountData.email
      });
      setAccountMessage('Datos guardados correctamente');
      // Actualizar usuario en contexto
      if (updateUserData) {
        updateUserData({ name: accountData.name, email: accountData.email });
      }
      setTimeout(() => setAccountMessage(''), 3000);
    } catch (error) {
      console.error('Error guardando:', error);
      setAccountMessage('Error al guardar los datos');
    } finally {
      setSavingAccount(false);
    }
  };
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showConfigDropdown, setShowConfigDropdown] = useState(false);

  // Estado de notificaciones (modo real)
  const [notifications, setNotifications] = useState([]);
  const unreadNotificationsCount = notifications.filter(n => !n.read).length;

  // Estado para submenú Configuración
  const [configMenuOpen, setConfigMenuOpen] = useState(false);

  // Estados para fichador
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);
  const [showFichadorModal, setShowFichadorModal] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Estado para submenú Departamentos
  const [deptMenuOpen, setDeptMenuOpen] = useState(true);

  // Estado para barra XIA (minimizada por defecto)
  const [xiaBarExpanded, setXiaBarExpanded] = useState(false);
  const [xiaBarVisible, setXiaBarVisible] = useState(true);
  const [xiaBarSize, setXiaBarSize] = useState(1); // 0=pequeño, 1=mediano, 2=grande
  const [xiaMessage, setXiaMessage] = useState('');
  const [xiaFile, setXiaFile] = useState(null);
  const [xiaFilePreview, setXiaFilePreview] = useState(null);
  const xiaFileInputRef = React.useRef(null);
  const xiaUserName = user?.full_name?.split(' ')[0] || '';
  const xiaGreeting = xiaUserName
    ? `¡Hola ${xiaUserName}! Soy XIA, ¿en qué puedo ayudarte?`
    : '¡Hola! Soy XIA, ¿en qué puedo ayudarte?';
  const [xiaMessages, setXiaMessages] = useState([
    { role: 'assistant', content: xiaGreeting }
  ]);
  const [xiaLoading, setXiaLoading] = useState(false);
  const xiaMessagesEndRef = React.useRef(null);
  const [xiaVoiceEnabled, setXiaVoiceEnabled] = useState(false);
  const [xiaListening, setXiaListening] = useState(false);
  const [xiaConversationMode, setXiaConversationMode] = useState(false);
  const [xiaSpeaking, setXiaSpeaking] = useState(false);
  const xiaRecognitionRef = React.useRef(null);
  const xiaCapturedTextRef = React.useRef('');

  // Inicializar reconocimiento de voz para barra XIA
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      xiaRecognitionRef.current = new SpeechRecognition();
      xiaRecognitionRef.current.continuous = false;
      xiaRecognitionRef.current.interimResults = true;
      xiaRecognitionRef.current.lang = 'es-ES';

      xiaRecognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          } else {
            interimTranscript += event.results[i][0].transcript;
          }
        }
        if (interimTranscript) {
          setXiaMessage(interimTranscript);
        }
        if (finalTranscript) {
          setXiaMessage(finalTranscript);
          xiaCapturedTextRef.current = finalTranscript;
        }
      };

      xiaRecognitionRef.current.onend = () => {
        setXiaListening(false);
        // En modo conversación, enviar automáticamente si hay texto
        if (xiaConversationMode && xiaCapturedTextRef.current.trim()) {
          const textToSend = xiaCapturedTextRef.current;
          xiaCapturedTextRef.current = '';
          setTimeout(() => {
            setXiaMessage(textToSend);
            setTimeout(() => {
              document.getElementById('xia-send-btn')?.click();
            }, 100);
          }, 300);
        }
      };

      xiaRecognitionRef.current.onerror = (event) => {
        console.error('Error de reconocimiento de voz:', event.error);
        setXiaListening(false);
        // En modo conversación, reintentar si no hubo speech
        if (xiaConversationMode && event.error === 'no-speech') {
          setTimeout(() => {
            if (xiaRecognitionRef.current && xiaConversationMode) {
              try {
                xiaRecognitionRef.current.start();
                setXiaListening(true);
              } catch (e) { /* ignore */ }
            }
          }, 500);
        }
      };
    }
    return () => {
      xiaRecognitionRef.current?.stop();
      window.speechSynthesis?.cancel();
    };
  }, [xiaConversationMode]);

  // Función para hablar respuesta de XIA
  const speakXiaResponse = (text) => {
    if (!xiaVoiceEnabled || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();

    // Limpiar texto para mejor pronunciación
    let cleanText = text
      .replace(/```[\s\S]*?```/g, ' código omitido ')
      .replace(/`[^`]+`/g, ' código ')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/#{1,6}\s*/g, '')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/https?:\/\/\S+/g, ' enlace ')
      .replace(/\n{2,}/g, '. ')
      .replace(/\n/g, '. ')
      .replace(/[•\-\*]\s+/g, '. ')
      .replace(/\s+/g, ' ')
      .trim();

    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.lang = 'es-ES';
    utterance.rate = 0.95;
    utterance.pitch = 1.1;
    const voices = window.speechSynthesis.getVoices();
    const esVoice = voices.find(v => v.lang.includes('es') && v.name.includes('Helena')) ||
                   voices.find(v => v.lang.includes('es'));
    if (esVoice) utterance.voice = esVoice;

    utterance.onstart = () => setXiaSpeaking(true);
    utterance.onend = () => {
      setXiaSpeaking(false);
      // En modo conversación, reactivar micrófono
      if (xiaConversationMode && xiaRecognitionRef.current) {
        setTimeout(() => {
          try {
            xiaRecognitionRef.current.start();
            setXiaListening(true);
          } catch (e) {
            console.error('Error reactivando micro:', e);
          }
        }, 500);
      }
    };
    utterance.onerror = () => setXiaSpeaking(false);

    window.speechSynthesis.speak(utterance);
  };

  // Toggle modo conversación
  const toggleXiaConversationMode = () => {
    if (xiaConversationMode) {
      // Desactivar
      setXiaConversationMode(false);
      setXiaVoiceEnabled(false);
      if (xiaRecognitionRef.current) xiaRecognitionRef.current.stop();
      window.speechSynthesis?.cancel();
      setXiaListening(false);
      setXiaSpeaking(false);
    } else {
      // Activar
      setXiaConversationMode(true);
      setXiaVoiceEnabled(true);
      setXiaBarExpanded(true);
      // Iniciar escucha
      if (xiaRecognitionRef.current) {
        try {
          xiaRecognitionRef.current.start();
          setXiaListening(true);
        } catch (e) {
          console.error('Error iniciando micro:', e);
        }
      }
    }
  };

  // Toggle micrófono XIA
  const toggleXiaListening = () => {
    if (xiaListening) {
      xiaRecognitionRef.current?.stop();
      setXiaListening(false);
    } else {
      try {
        xiaRecognitionRef.current?.start();
        setXiaListening(true);
      } catch (e) {
        console.error('Error iniciando micro:', e);
      }
    }
  };

  // Alturas de la barra XIA expandida
  const xiaBarHeights = ['h-48', 'h-80', 'h-[28rem]'];
  const xiaContentHeights = ['h-20', 'h-52', 'h-80'];

  // Scroll al final de mensajes XIA
  useEffect(() => {
    xiaMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [xiaMessages]);

  // Enviar mensaje a XIA
  const handleXIASend = async () => {
    if (!xiaMessage.trim() || xiaLoading) return;

    const userMsg = xiaMessage.trim();
    setXiaMessage('');
    setXiaBarExpanded(true);

    const newMessages = [...xiaMessages, { role: 'user', content: userMsg }];
    setXiaMessages(newMessages);
    setXiaLoading(true);

    try {
      const conversationHistory = newMessages.slice(1).map(m => ({
        role: m.role,
        content: m.content
      }));

      const response = await chatWithAI(userMsg, conversationHistory, '');
      const aiResponse = response.data.response;
      setXiaMessages([...newMessages, { role: 'assistant', content: aiResponse }]);

      // Leer respuesta en voz alta si está activado
      speakXiaResponse(aiResponse);

      // Si XIA creó una tarea, recargar la lista de tareas
      if (response.data.task) {
        window.dispatchEvent(new Event('task:reload'));
      }
    } catch (err) {
      console.error('Error XIA:', err);
      setXiaMessages([...newMessages, {
        role: 'assistant',
        content: 'Lo siento, hubo un error al conectar. ¿Puedes intentarlo de nuevo?'
      }]);
    } finally {
      setXiaLoading(false);
    }
  };

  // Manejar selección de archivo para XIA
  const handleXiaFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setXiaFile(file);

    // Crear preview si es imagen
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setXiaFilePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setXiaFilePreview(null);
    }
  };

  // Limpiar archivo seleccionado
  const clearXiaFile = () => {
    setXiaFile(null);
    setXiaFilePreview(null);
    if (xiaFileInputRef.current) xiaFileInputRef.current.value = '';
  };

  // Detectar si es movil
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadDepartments();
  }, []);

  // Cargar notificaciones y configurar socket
  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const res = await getNotifications();
        if (res.data.notifications) {
          setNotifications(res.data.notifications);
        }
      } catch (error) {
        console.error('Error cargando notificaciones:', error);
      }
    };

    loadNotifications();

    // Conectar socket y escuchar notificaciones nuevas
    if (user?.id) {
      connectSocket(user.id, user.departments || []);

      const handleNewNotification = (notification) => {
        setNotifications(prev => [notification, ...prev]);
      };

      onNewNotification(handleNewNotification);

      return () => {
        offNewNotification(handleNewNotification);
      };
    }
  }, [user?.id]);

  const loadDepartments = async () => {
    try {
      const response = await getDepartments();
      const allDepts = response.data.departments;

      // Filtrar departamentos a los que tiene acceso
      const accessibleDepts = allDepts.filter(dept =>
        hasDepartmentAccess(dept.id)
      );

      setDepartments(accessibleDepts);

      // Seleccionar primer departamento por defecto
      if (accessibleDepts.length > 0 && !selectedDepartment) {
        setSelectedDepartment(accessibleDepts[0]);
      }
    } catch (error) {
      console.error('Error cargando departamentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Actualizar reloj cada segundo
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Cargar estado inicial del fichador
  useEffect(() => {
    const loadFichadorStatus = async () => {
      try {
        const res = await getFichadorStatus();
        if (res.data.success) {
          setIsClockedIn(res.data.status === 'working' || res.data.status === 'break');
          setIsOnBreak(res.data.status === 'break');
          if (res.data.clockInTime) {
            setClockInTime(new Date(res.data.clockInTime));
          }
        }
      } catch (err) {
        console.error('Error cargando estado fichador:', err);
      }
    };
    loadFichadorStatus();
  }, []);

  // Funciones fichador
  const handleClockIn = async () => {
    try {
      const res = await fichadorClockIn();
      if (res.data.success) {
        setIsClockedIn(true);
        setClockInTime(new Date());
        setIsOnBreak(false);
      }
    } catch (err) {
      console.error('Error fichando entrada:', err);
    }
  };

  const handleClockOut = async () => {
    try {
      const res = await fichadorClockOut();
      if (res.data.success) {
        setIsClockedIn(false);
        setClockInTime(null);
        setIsOnBreak(false);
        // Mostrar mensaje con horas trabajadas
        if (res.data.totalWorked) {
          alert(res.data.message);
        }
      }
    } catch (err) {
      console.error('Error fichando salida:', err);
    }
  };

  const handleBreakToggle = async () => {
    try {
      if (isOnBreak) {
        const res = await fichadorBreakEnd();
        if (res.data.success) {
          setIsOnBreak(false);
        }
      } else {
        const res = await fichadorBreakStart('descanso');
        if (res.data.success) {
          setIsOnBreak(true);
        }
      }
    } catch (err) {
      console.error('Error cambiando estado descanso:', err);
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const getIcon = (deptName) => {
    const Icon = departmentIcons[deptName] || MessageSquare;
    return Icon;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <img
            src="/logo-infoticketing.png"
            alt="Infoticketing"
            className="h-20 mx-auto animate-pulse"
          />
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex">
      {/* Overlay para cerrar sidebar en movil */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - SIEMPRE FIJO */}
      <aside className={`bg-white border-r border-gray-400 transition-all duration-300 flex flex-col fixed inset-y-0 left-0 z-30
        ${isMobile
          ? `z-50 w-64 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`
          : `${sidebarOpen ? 'w-64' : 'w-20'}`
        }`}>
        {/* Header */}
        <div className="border-b border-gray-400">
          {sidebarOpen ? (
            <div className="relative">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="absolute top-3 right-3 p-2 hover:bg-gray-50 rounded-lg text-[#6B7280] transition-colors duration-200"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex justify-center py-6 px-4">
                <img
                  src="/logo-infoticketing.png"
                  alt="Infoticketing"
                  className="h-20"
                />
              </div>
            </div>
          ) : (
            <div className="p-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 hover:bg-gray-50 rounded-lg w-full flex justify-center text-[#6B7280] transition-colors duration-200"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Main Navigation */}
        <div className="py-4">

          {/* Agenda */}
          <button
            onClick={() => setCurrentView('agenda')}
            className={`w-full flex items-center pl-4 py-3 transition-all duration-200 ${
              currentView === 'agenda' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
            }`}
          >
            <Calendar className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3">Agenda</span>}
          </button>
          {sidebarOpen && <div className="mx-3 h-px bg-gray-400" />}

          {/* Server */}
          <button
            onClick={() => setCurrentView('server')}
            className={`w-full flex items-center pl-4 py-3 transition-all duration-200 ${
              currentView === 'server' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
            }`}
          >
            <HardDrive className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3">Server</span>}
          </button>
          {sidebarOpen && <div className="mx-3 h-px bg-gray-400" />}

          {/* Chat */}
          <button
            onClick={() => {
              setCurrentView('chat');
              if (!selectedDepartment && departments.length > 0) {
                setSelectedDepartment(departments[0]);
              }
            }}
            className={`w-full flex items-center pl-4 py-3 transition-all duration-200 ${
              currentView === 'chat' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
            }`}
          >
            <MessageSquare className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3">Chat</span>}
          </button>
          {sidebarOpen && <div className="mx-3 h-px bg-gray-400" />}

          {/* Contactos */}
          <button
            onClick={() => setCurrentView('contactos')}
            className={`w-full flex items-center pl-4 py-3 transition-all duration-200 ${
              currentView === 'contactos' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
            }`}
          >
            <Users className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3">Contactos</span>}
          </button>

          {/* XIA - Solo superadmin */}
          {user?.role === 'superadmin' && (
            <>
              <button
                onClick={() => setCurrentView('ai')}
                className={`w-full flex items-center pl-4 py-3 transition-all duration-200 ${
                  currentView === 'ai' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                }`}
              >
                <img src="/xia-icon.png" alt="XIA" className="w-6 h-6 object-contain" />
                {sidebarOpen && <span className="ml-3">XIA</span>}
              </button>
              {sidebarOpen && <div className="mx-3 h-px bg-gray-400" />}
            </>
          )}

          {/* DEPARTAMENTOS - Menu desplegable solo para superadmin/admin */}
          {['superadmin', 'admin'].includes(user?.role) ? (
            <div className="space-y-1">
              <button
                onClick={() => setDeptMenuOpen(!deptMenuOpen)}
                className="w-full flex items-center pl-4 py-3 transition-all duration-200 hover:bg-gray-50 text-[#6B7280]"
              >
                <Building2 className="w-5 h-5" />
                {sidebarOpen && (
                  <>
                    <span className="ml-3 flex-1 text-left font-medium">Departamentos</span>
                    {deptMenuOpen ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </>
                )}
              </button>

              {/* Submenu Departamentos */}
              {deptMenuOpen && sidebarOpen && (
                <div className="ml-4 space-y-0.5 border-l border-gray-200 pl-3">
                  {/* Gerencia */}
                  <button
                    onClick={() => setCurrentView('gerencia')}
                    className={`w-full flex items-center p-2.5 rounded-lg transition-all duration-200 text-sm ${
                      currentView === 'gerencia' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                    }`}
                  >
                    <Crown className="w-4 h-4" />
                    <span className="ml-2">Gerencia</span>
                  </button>

                  {/* Administracion */}
                  <button
                    onClick={() => setCurrentView('administracion')}
                    className={`w-full flex items-center p-2.5 rounded-lg transition-all duration-200 text-sm ${
                      currentView === 'administracion' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    <span className="ml-2">Administracion</span>
                  </button>

                  {/* Comercial */}
                  <button
                    onClick={() => setCurrentView('comercial')}
                    className={`w-full flex items-center p-2.5 rounded-lg transition-all duration-200 text-sm ${
                      currentView === 'comercial' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                    }`}
                  >
                    <Briefcase className="w-4 h-4" />
                    <span className="ml-2">Comercial</span>
                  </button>

                  {/* SAT */}
                  <button
                    onClick={() => setCurrentView('sat')}
                    className={`w-full flex items-center p-2.5 rounded-lg transition-all duration-200 text-sm ${
                      currentView === 'sat' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                    }`}
                  >
                    <Ticket className="w-4 h-4" />
                    <span className="ml-2">SAT</span>
                  </button>

                  {/* Proyectos */}
                  <button
                    onClick={() => setCurrentView('proyectos')}
                    className={`w-full flex items-center p-2.5 rounded-lg transition-all duration-200 text-sm ${
                      currentView === 'proyectos' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                    }`}
                  >
                    <FolderKanban className="w-4 h-4" />
                    <span className="ml-2">Proyectos</span>
                  </button>

                  {/* Franquicias */}
                  <button
                    onClick={() => setCurrentView('franquicias')}
                    className={`w-full flex items-center p-2.5 rounded-lg transition-all duration-200 text-sm ${
                      currentView === 'franquicias' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                    }`}
                  >
                    <Building2 className="w-4 h-4" />
                    <span className="ml-2">Franquicias</span>
                  </button>

                  {/* Portal Cliente */}
                  <button
                    onClick={() => setCurrentView('clientes')}
                    className={`w-full flex items-center p-2.5 rounded-lg transition-all duration-200 text-sm ${
                      currentView === 'clientes' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                    }`}
                  >
                    <UserCircle className="w-4 h-4" />
                    <span className="ml-2">Portal Cliente</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* Usuarios de departamento - acceso directo sin dropdown */
            <div className="space-y-1">
              {/* Comercial - usuario, revisor de comercial */}
              {['usuario', 'revisor'].includes(user?.role) && (
                <button
                  onClick={() => setCurrentView('comercial')}
                  className={`w-full flex items-center pl-4 py-3 transition-all duration-200 ${
                    currentView === 'comercial' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                  }`}
                >
                  <Briefcase className="w-5 h-5" />
                  {sidebarOpen && <span className="ml-3">Comercial</span>}
                </button>
              )}

              {/* SAT - tecnico_externo */}
              {user?.role === 'tecnico_externo' && (
                <button
                  onClick={() => setCurrentView('sat')}
                  className={`w-full flex items-center pl-4 py-3 transition-all duration-200 ${
                    currentView === 'sat' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                  }`}
                >
                  <Ticket className="w-5 h-5" />
                  {sidebarOpen && <span className="ml-3">SAT</span>}
                </button>
              )}

              {/* Franquicias - franquiciado */}
              {user?.role === 'franquiciado' && (
                <button
                  onClick={() => setCurrentView('franquicias')}
                  className={`w-full flex items-center pl-4 py-3 transition-all duration-200 ${
                    currentView === 'franquicias' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                  }`}
                >
                  <Building2 className="w-5 h-5" />
                  {sidebarOpen && <span className="ml-3">Franquicias</span>}
                </button>
              )}

              {/* Portal Cliente - cliente_final, cuenta_cliente */}
              {['cliente_final', 'cuenta_cliente'].includes(user?.role) && (
                <button
                  onClick={() => setCurrentView('clientes')}
                  className={`w-full flex items-center pl-4 py-3 transition-all duration-200 ${
                    currentView === 'clientes' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                  }`}
                >
                  <UserCircle className="w-5 h-5" />
                  {sidebarOpen && <span className="ml-3">Portal Cliente</span>}
                </button>
              )}
            </div>
          )}

          {/* Almacen - Solo admin y superadmin */}
          {['superadmin', 'admin'].includes(user?.role) && (
            <button
              onClick={() => setCurrentView('almacen')}
              className={`w-full flex items-center pl-4 py-3 transition-all duration-200 ${
                currentView === 'almacen' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
              }`}
            >
              <Package className="w-5 h-5" />
              {sidebarOpen && <span className="ml-3">Almacen</span>}
            </button>
          )}

          {/* Chats Externos - Admin y superadmin */}
          {['superadmin', 'admin'].includes(user?.role) && (
            <button
              onClick={() => setCurrentView('franquiciados')}
              className={`w-full flex items-center pl-4 py-3 transition-all duration-200 ${
                currentView === 'franquiciados' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
              }`}
            >
              <Headphones className="w-5 h-5" />
              {sidebarOpen && <span className="ml-3">Chats Externos</span>}
            </button>
          )}

          {/* Separador antes de Configuración - estilo Server */}
          {sidebarOpen && ['superadmin', 'admin', 'usuario', 'revisor'].includes(user?.role) && (
            <div className="my-3 mx-3">
              <div className="flex items-center gap-2">
                <div className="w-1 h-4 bg-purple-500 rounded-full" />
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Sistema</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
            </div>
          )}

          {/* CONFIGURACIÓN - Menú desplegable (admin, superadmin, usuario, revisor) */}
          {['superadmin', 'admin', 'usuario', 'revisor'].includes(user?.role) && (
            <div className="space-y-1">
              <button
                onClick={() => setConfigMenuOpen(!configMenuOpen)}
                className="w-full flex items-center pl-4 py-3 transition-all duration-200 hover:bg-gray-50 text-[#6B7280]"
              >
                <Settings className="w-5 h-5" />
                {sidebarOpen && (
                  <>
                    <span className="ml-3 flex-1 text-left">Configuracion</span>
                    {configMenuOpen ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </>
                )}
              </button>

              {/* Submenú Configuración - permisos por rol */}
              {configMenuOpen && sidebarOpen && (
                <div className="ml-4 space-y-0.5 border-l border-gray-200 pl-3">
                  {/* Mi Cuenta - todos los usuarios */}
                  <button
                    onClick={() => setCurrentView('account')}
                    className={`w-full flex items-center p-2.5 rounded-lg transition-all duration-200 text-sm ${
                      currentView === 'account' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                    }`}
                  >
                    <Shield className="w-4 h-4" />
                    <span className="ml-2">Mi Cuenta</span>
                  </button>
                  {/* Usuarios - solo admin/superadmin */}
                  {['superadmin', 'admin'].includes(user?.role) && (
                    <button
                      onClick={() => setCurrentView('users')}
                      className={`w-full flex items-center p-2.5 rounded-lg transition-all duration-200 text-sm ${
                        currentView === 'users' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                      }`}
                    >
                      <Users className="w-4 h-4" />
                      <span className="ml-2">Usuarios</span>
                    </button>
                  )}
                  {/* Conocimiento IA - admin/superadmin/revisor */}
                  {['superadmin', 'admin', 'revisor'].includes(user?.role) && (
                    <button
                      onClick={() => setCurrentView('knowledge')}
                      className={`w-full flex items-center p-2.5 rounded-lg transition-all duration-200 text-sm ${
                        currentView === 'knowledge' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                      }`}
                    >
                      <BookOpen className="w-4 h-4" />
                      <span className="ml-2">Conocimiento IA</span>
                    </button>
                  )}
                  {/* Datos Aparatos - solo admin/superadmin */}
                  {['superadmin', 'admin'].includes(user?.role) && (
                    <button
                      onClick={() => setCurrentView('aparatos')}
                      className={`w-full flex items-center p-2.5 rounded-lg transition-all duration-200 text-sm ${
                        currentView === 'aparatos' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                      }`}
                    >
                      <Database className="w-4 h-4" />
                      <span className="ml-2">Datos Aparatos</span>
                    </button>
                  )}
                  {/* Archivos - todos */}
                  <button
                    onClick={() => setCurrentView('files')}
                    className={`w-full flex items-center p-2.5 rounded-lg transition-all duration-200 text-sm ${
                      currentView === 'files' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                    }`}
                  >
                    <FolderOpen className="w-4 h-4" />
                    <span className="ml-2">Archivos</span>
                  </button>
                  {/* Incidencias - solo admin/superadmin */}
                  {['superadmin', 'admin'].includes(user?.role) && (
                    <button
                      onClick={() => setCurrentView('tickets')}
                      className={`w-full flex items-center p-2.5 rounded-lg transition-all duration-200 text-sm ${
                        currentView === 'tickets' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                      }`}
                    >
                      <AlertTriangle className="w-4 h-4" />
                      <span className="ml-2">Incidencias</span>
                    </button>
                  )}
                  {/* Pantallas - solo admin/superadmin */}
                  {['superadmin', 'admin'].includes(user?.role) && (
                    <button
                      onClick={() => setCurrentView('screens')}
                      className={`w-full flex items-center p-2.5 rounded-lg transition-all duration-200 text-sm ${
                        currentView === 'screens' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                      }`}
                    >
                      <MapPin className="w-4 h-4" />
                      <span className="ml-2">Pantallas</span>
                    </button>
                  )}
                  {/* Chat Config - todos */}
                  <button
                    onClick={() => setCurrentView('chatConfig')}
                    className={`w-full flex items-center p-2.5 rounded-lg transition-all duration-200 text-sm ${
                      currentView === 'chatConfig' ? 'bg-[#3B82F6]/10 text-[#3B82F6] font-medium' : 'hover:bg-gray-50 text-[#6B7280]'
                    }`}
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span className="ml-2">Config Chat</span>
                  </button>
                  {/* Preferencias - todos */}
                  <button
                    onClick={() => setShowSettings(prev => !prev)}
                    className="w-full flex items-center p-2.5 rounded-lg transition-all duration-200 text-sm hover:bg-gray-50 text-[#6B7280]"
                  >
                    <Sliders className="w-4 h-4" />
                    <span className="ml-2">Preferencias</span>
                  </button>
                  {/* Fichador - todos */}
                  <button
                    onClick={() => window.open('/fichador', '_blank')}
                    className="w-full flex items-center p-2.5 rounded-lg transition-all duration-200 text-sm hover:bg-gray-50 text-[#6B7280]"
                  >
                    <Clock className="w-4 h-4" />
                    <span className="ml-2">Fichador</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Departments */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {currentView === 'chat' && departments.map(dept => {
            const Icon = getIcon(dept.name);
            const isSelected = selectedDepartment?.id === dept.id;
            const deptColor = getDeptColor(dept.name);

            return (
              <button
                key={dept.id}
                onClick={() => setSelectedDepartment(dept)}
                className={`w-full flex items-center pl-4 py-3 transition-all duration-200 relative overflow-hidden ${
                  isSelected
                    ? `${deptColor.light} ${deptColor.text} font-medium border-l-4 ${deptColor.border}`
                    : 'hover:bg-gray-50 text-[#6B7280] border-l-4 border-transparent'
                }`}
              >
                {/* Franja de color sutil */}
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${isSelected ? deptColor.bg : 'bg-transparent'} transition-all`} />
                <Icon className={`w-5 h-5 ${isSelected ? deptColor.text : ''}`} />
                {sidebarOpen && <span className="ml-3">{dept.name}</span>}
              </button>
            );
          })}
        </nav>

        {/* User */}
        <div className="border-t border-gray-400" style={{ marginTop: '-100px' }}>
          {/* CAJÓN DE DEPARTAMENTO - pegado encima del fichador */}
          {sidebarOpen && (() => {
            // Obtener nombre del departamento de múltiples fuentes posibles
            let deptName = user?.department ||
                           user?.departments?.[0] ||
                           user?.user_departments?.[0]?.department?.name ||
                           user?.user_departments?.[0]?.departments?.name ||
                           user?.user_departments?.[0]?.name;

            // Si es string, usarlo directamente
            if (typeof user?.departments?.[0] === 'object' && user?.departments?.[0]?.name) {
              deptName = user.departments[0].name;
            }

            // Mapear nombres de departamento
            const deptNameMap = {
              'comercial': 'COMERCIAL',
              'sat': 'SAT',
              'soporte': 'SAT',
              'soporte_tecnico': 'SAT',
              'soporte tecnico': 'SAT',
              'gerencia': 'GERENCIA',
              'administracion': 'ADMINISTRACIÓN',
              'produccion': 'PRODUCCIÓN',
              'desarrollo': 'DESARROLLO',
              'marketing': 'MARKETING',
              'rrhh': 'RRHH',
              'proyectos': 'PROYECTOS',
              'almacen': 'ALMACÉN'
            };

            // Normalizar nombre
            if (deptName) {
              const normalized = deptName.toLowerCase().replace(/[_\s]+/g, '_');
              for (const [key, value] of Object.entries(deptNameMap)) {
                if (normalized.includes(key) || key.includes(normalized)) {
                  deptName = value;
                  break;
                }
              }
            }

            // Si no hay departamento, intentar deducir del rol
            // CAMBIO: superadmin/admin ven COMERCIAL por defecto (no GERENCIA)
            if (!deptName) {
              if (user?.role === 'superadmin' || user?.role === 'admin') deptName = 'COMERCIAL';
              else if (user?.role === 'usuario' || user?.role === 'revisor') deptName = 'COMERCIAL';
              else if (user?.role === 'tecnico_externo') deptName = 'SAT';
              else if (user?.role === 'franquiciado') deptName = 'FRANQUICIAS';
              else if (user?.role === 'cliente_final' || user?.role === 'cuenta_cliente') deptName = 'CLIENTES';
              else deptName = 'COMERCIAL'; // Fallback a COMERCIAL en vez de null
            }

            if (!deptName) return null;

            const deptColor = getDeptColor(deptName.replace('Ó', 'o').replace('É', 'e').replace('Á', 'a'));
            return (
              <div
                className="py-3 px-4 text-center"
                style={{ backgroundColor: deptColor?.hex || '#3B82F6' }}
              >
                <span className="text-white font-bold text-base uppercase tracking-widest drop-shadow-md">
                  {deptName}
                </span>
              </div>
            );
          })()}

          {/* Avatar y Usuario - ARRIBA del fichador */}
          <div className="p-4 pb-6">
          <div className="flex items-center mb-4">
            {(() => {
              const avatarUrl = user?.avatar_url;
              // Si es una foto (base64 o URL)
              if (avatarUrl && (avatarUrl.startsWith('data:') || avatarUrl.startsWith('http'))) {
                return (
                  <img
                    src={avatarUrl}
                    alt={user?.name || 'Avatar'}
                    className={`w-14 h-14 rounded-full object-cover border-3 shadow-lg ${
                      isClockedIn ? (isOnBreak ? 'border-yellow-500' : 'border-green-500') : 'border-[#3B82F6]'
                    }`}
                  />
                );
              }
              // Si es un avatar predefinido (formato: avatar:id:color)
              if (avatarUrl && avatarUrl.startsWith('avatar:')) {
                const parts = avatarUrl.split(':');
                const avatarId = parts[1];
                const color = parts[2] || '#3B82F6';
                return (
                  <div className={`w-14 h-14 rounded-full overflow-hidden border-3 shadow-lg ${
                    isClockedIn ? (isOnBreak ? 'border-yellow-500' : 'border-green-500') : 'border-[#3B82F6]'
                  }`}>
                    <AvatarSVG avatarId={avatarId} color={color} size={56} />
                  </div>
                );
              }
              // Fallback: inicial con color por defecto
              return (
                <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg ${
                  isClockedIn ? (isOnBreak ? 'bg-yellow-500' : 'bg-green-500') : 'bg-[#3B82F6]'
                }`}>
                  {user?.name?.charAt(0) || user?.full_name?.charAt(0) || 'U'}
                </div>
              );
            })()}
            {sidebarOpen && (
              <div className="ml-3 flex-1">
                <p className="text-sm font-semibold text-[#111827]">{user?.name || 'Usuario'}</p>
                <p className="text-xs text-[#6B7280]">{roleLabels[user?.role] || user?.role || 'Sin rol'}</p>
              </div>
            )}
          </div>

          {/* Fichador mini - DEBAJO del avatar */}
          {sidebarOpen && (
            <div className="p-2 bg-gray-50 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-[#6B7280]">Fichador</span>
                <span className="text-sm font-mono font-semibold text-[#111827]">
                  {formatTime(currentTime)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {!isClockedIn ? (
                  <button
                    onClick={handleClockIn}
                    className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 bg-green-500 text-white text-xs font-medium rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <Play className="w-3 h-3" />
                    Entrada
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleBreakToggle}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        isOnBreak
                          ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                      title={isOnBreak ? 'Volver' : 'Descanso'}
                    >
                      <Coffee className="w-3 h-3" />
                      {isOnBreak ? 'Volver' : 'Pausa'}
                    </button>
                    <button
                      onClick={handleClockOut}
                      className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-600 transition-colors"
                    >
                      <Pause className="w-3 h-3" />
                      Salida
                    </button>
                  </>
                )}
              </div>
              {isClockedIn && clockInTime && (
                <p className="text-[10px] text-center text-[#9CA3AF] mt-1.5">
                  {isOnBreak ? 'En pausa' : 'Trabajando'} desde {formatTime(clockInTime)}
                </p>
              )}
            </div>
          )}
          </div>
        </div>
      </aside>

      {/* Main Content - con margen para sidebar fijo */}
      <main className={`flex-1 flex flex-col transition-all duration-300 ${
        isMobile ? 'ml-0' : (sidebarOpen ? 'ml-64' : 'ml-20')
      }`}>
        {/* Top Bar - Oculto en vista Chat para pantalla completa */}
        {currentView !== 'chat' && (
        <header className="bg-white border-b border-gray-400 relative">
          {/* Franja de color del departamento activo */}
          {(currentView === 'chat' && selectedDepartment) || ['gerencia', 'administracion', 'comercial', 'sat', 'proyectos', 'almacen', 'franquicias', 'clientes'].includes(currentView) ? (
            <div className={`absolute top-0 left-0 right-0 h-1 ${
              currentView === 'gerencia' ? 'bg-red-500' :
              currentView === 'administracion' ? 'bg-blue-500' :
              currentView === 'comercial' ? 'bg-green-500' :
              currentView === 'sat' ? 'bg-orange-500' :
              currentView === 'proyectos' ? 'bg-indigo-500' :
              currentView === 'almacen' ? 'bg-cyan-500' :
              currentView === 'franquicias' ? 'bg-pink-500' :
              currentView === 'clientes' ? 'bg-teal-500' :
              selectedDepartment ? getDeptColor(selectedDepartment.name).bg : 'bg-gray-300'
            }`} />
          ) : null}
          <div className="px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            {/* Logo en movil (arriba izquierda) */}
            {isMobile && (
              <img
                src="/logo-infoticketing.png"
                alt="Infoticketing"
                className="h-7 mr-3"
              />
            )}
            {/* Menu hamburguesa en movil */}
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 hover:bg-gray-50 rounded-lg mr-2 transition-colors duration-200"
              >
                <Menu className="w-5 h-5 text-[#6B7280]" />
              </button>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg md:text-xl font-semibold text-[#111827] truncate">
              {currentView === 'users' ? 'Usuarios' :
               currentView === 'ai' ? 'Xavia' :
               currentView === 'knowledge' ? 'Conocimiento IA' :
               currentView === 'aparatos' ? 'Datos de Aparatos' :
               currentView === 'franquiciados' ? 'Chats Externos' :
               currentView === 'tickets' ? 'Incidencias' :
               currentView === 'files' ? 'Archivos' :
               currentView === 'screens' ? 'Pantallas' :
               currentView === 'gerencia' ? 'Gerencia' :
               currentView === 'administracion' ? 'Administracion' :
               currentView === 'comercial' ? 'Comercial' :
               currentView === 'sat' ? 'SAT' :
               currentView === 'proyectos' ? 'Proyectos' :
               currentView === 'almacen' ? 'Almacen' :
               currentView === 'franquicias' ? 'Franquicias' :
               currentView === 'clientes' ? 'Portal Cliente' :
               currentView === 'agenda' ? 'Agenda' :
               currentView === 'server' ? 'Server' :
               currentView === 'contactos' ? 'Contactos' :
               currentView === 'account' ? 'Mi Cuenta' :
               (selectedDepartment?.name || '')}
            </h1>
            <p className="text-xs md:text-sm text-[#6B7280] truncate hidden sm:block">
              {currentView === 'users' ? 'Gestiona los usuarios del sistema' :
               currentView === 'ai' ? 'Chatea con Xavia, tu asistente inteligente' :
               currentView === 'knowledge' ? 'Gestiona la informacion que Xavia utiliza' :
               currentView === 'aparatos' ? 'Importa datos de taquillas, maquinas y aparatos para Xavia' :
               currentView === 'franquiciados' ? 'URLs de chat para franquiciados, lugares y clientes' :
               currentView === 'tickets' ? 'Incidencias de aparatos, chat, WhatsApp y telefono' :
               currentView === 'files' ? 'Carpetas por departamento, compartidas y para franquiciados' :
               currentView === 'screens' ? 'Pantallas publicas para mostrar en monitores del taller/oficina' :
               currentView === 'gerencia' ? 'Dashboard ejecutivo con KPIs y metricas' :
               currentView === 'administracion' ? 'Facturas, contabilidad y recursos humanos' :
               currentView === 'comercial' ? 'CRM y gestion de ventas' :
               currentView === 'sat' ? 'Servicio tecnico e incidencias' :
               currentView === 'proyectos' ? 'Gestion de proyectos y tareas' :
               currentView === 'almacen' ? 'Stock e inventario' :
               currentView === 'franquicias' ? 'Gestion de la red de franquicias' :
               currentView === 'clientes' ? 'Tu espacio de cliente' :
               currentView === 'agenda' ? 'Tareas, trabajos y calendario' :
               currentView === 'server' ? 'Archivos compartidos y recursos' :
               currentView === 'contactos' ? 'Directorio de contactos' :
               currentView === 'account' ? 'Seguridad, contraseña y conexiones' :
               (selectedDepartment?.description || '')}
            </p>
          </div>
          <div className="flex items-center space-x-2 md:space-x-4">
            {/* Botón Notificaciones */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-2 hover:bg-gray-50 rounded-lg relative transition-colors duration-200"
              >
                <Bell className="w-5 h-5 text-[#6B7280]" />
                {unreadNotificationsCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#3B82F6] text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {unreadNotificationsCount}
                  </span>
                )}
              </button>
              {showNotifications && (
                <NotificationsDropdown
                  notifications={notifications}
                  onNotificationsChange={setNotifications}
                  onClose={() => setShowNotifications(false)}
                  onNavigate={(view, deptId) => {
                    if (view === 'chat' && deptId) {
                      const dept = departments.find(d => d.id === deptId);
                      if (dept) {
                        setSelectedDepartment(dept);
                        setCurrentView('chat');
                      }
                    } else {
                      setCurrentView(view);
                    }
                  }}
                  onOpenXIA={(message) => {
                    // Abrir XIA y enviar mensaje
                    setXiaMessage(message);
                    setXiaBarExpanded(true);
                    // Enviar automáticamente después de un breve delay
                    setTimeout(() => {
                      document.getElementById('xia-send-btn')?.click();
                    }, 100);
                  }}
                />
              )}
            </div>
            {/* Botón Configuración con Dropdown - CSS hover */}
            <div className="relative group">
              <button
                className="p-2 hover:bg-gray-50 rounded-lg transition-colors duration-200"
              >
                <Settings className="w-5 h-5 text-[#6B7280]" />
              </button>
              {/* Dropdown aparece con group-hover */}
              <div className="absolute right-0 top-full w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50 hidden group-hover:block">
                  <div className="px-3 py-2 border-b border-gray-400">
                    <p className="text-xs font-semibold text-gray-400 uppercase">Configuración</p>
                  </div>
                  {/* Mi Cuenta */}
                  <button
                    onClick={() => { setCurrentView('account');  }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left"
                  >
                    <Shield className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700">Mi Cuenta</span>
                  </button>
                  {/* Mis Archivos - almacenamiento personal 20GB */}
                  <button
                    onClick={() => { setCurrentView('myFiles');  }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left"
                  >
                    <HardDrive className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700">Mis Archivos</span>
                    <span className="ml-auto text-xs text-gray-400">20GB</span>
                  </button>
                  {/* Usuarios - solo admin/superadmin */}
                  {['superadmin', 'admin'].includes(user?.role) && (
                    <button
                      onClick={() => { setCurrentView('users');  }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left"
                    >
                      <Users className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-700">Usuarios</span>
                    </button>
                  )}
                  {/* Conocimiento IA */}
                  {['superadmin', 'admin', 'revisor'].includes(user?.role) && (
                    <button
                      onClick={() => { setCurrentView('knowledge');  }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left"
                    >
                      <BookOpen className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-700">Conocimiento IA</span>
                    </button>
                  )}
                  {/* Datos Aparatos */}
                  {['superadmin', 'admin'].includes(user?.role) && (
                    <button
                      onClick={() => { setCurrentView('aparatos');  }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left"
                    >
                      <Database className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-700">Datos Aparatos</span>
                    </button>
                  )}
                  {/* Archivos */}
                  <button
                    onClick={() => { setCurrentView('files');  }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left"
                  >
                    <FolderOpen className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700">Archivos</span>
                  </button>
                  {/* Incidencias */}
                  {['superadmin', 'admin'].includes(user?.role) && (
                    <button
                      onClick={() => { setCurrentView('tickets');  }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left"
                    >
                      <AlertTriangle className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-700">Incidencias</span>
                    </button>
                  )}
                  {/* Pantallas */}
                  {['superadmin', 'admin'].includes(user?.role) && (
                    <button
                      onClick={() => { setCurrentView('screens');  }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left"
                    >
                      <MapPin className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-700">Pantallas</span>
                    </button>
                  )}
                  <div className="border-t border-gray-400 my-1"></div>
                  {/* Preferencias */}
                  <button
                    onClick={() => { setShowSettings(prev => !prev);  }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left"
                  >
                    <Sliders className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700">Preferencias</span>
                  </button>
                  {/* Fichador */}
                  <button
                    onClick={() => window.open('/fichador', '_blank')}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left"
                  >
                    <Clock className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700">Fichador</span>
                  </button>
              </div>
            </div>
            {/* Botón Salir */}
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-red-50 rounded-lg transition-colors duration-200 group"
              title="Cerrar sesión"
            >
              <LogOut className="w-5 h-5 text-[#6B7280] group-hover:text-red-600" />
            </button>
          </div>
          </div>
        </header>
        )}

        {/* Settings Panel */}
        {showSettings && (
          <SettingsPanel
                user={user}
                onClose={() => setShowSettings(false)}
                onAvatarChange={(avatarData) => {
                  // Construir avatar_url según el tipo
                  let avatarUrl = null;
                  if (avatarData.type === 'photo') {
                    avatarUrl = avatarData.photo;
                  } else if (avatarData.type === 'avatar') {
                    avatarUrl = `avatar:${avatarData.avatarId}:${avatarData.color}`;
                  }
                  updateUserData({ avatar_url: avatarUrl });
                }}
              />
        )}

        {/* Content Area */}
        <div className={`flex-1 overflow-hidden ${currentView !== 'chat' && currentView !== 'ai' ? `overflow-y-auto ${xiaBarExpanded ? 'pb-80' : 'pb-16'}` : ''}`}>
          {currentView === 'users' ? (
            <UserManagement currentUser={user} />
          ) : currentView === 'ai' || currentView === 'chat' ? (
            <div className="h-full flex flex-col">
              <AIChat user={user} />
            </div>
          ) : currentView === 'knowledge' ? (
            <KnowledgeManager departments={departments} />
          ) : currentView === 'aparatos' ? (
            <AparatosManager user={user} />
          ) : currentView === 'franquiciados' ? (
            <FranquiciadosManager />
          ) : currentView === 'tickets' ? (
            <TicketsManager currentUser={user} />
          ) : currentView === 'files' ? (
            <FileManager user={user} departments={departments} />
          ) : currentView === 'screens' ? (
            <ScreensManager />
          ) : currentView === 'gerencia' ? (
            <GerenciaPanel />
          ) : currentView === 'administracion' ? (
            <AdministracionPanel />
          ) : currentView === 'comercial' ? (
            <ComercialPanel />
          ) : currentView === 'sat' ? (
            <SATPanel />
          ) : currentView === 'proyectos' ? (
            <ProyectosPanel />
          ) : currentView === 'almacen' ? (
            <AlmacenPanel />
          ) : currentView === 'franquicias' ? (
            <FranquiciasPanel />
          ) : currentView === 'clientes' ? (
            <ClientesPanel />
          ) : currentView === 'chatConfig' ? (
            <div className="h-full p-6">
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center gap-3 mb-6">
                  <MessageSquare className="w-8 h-8 text-blue-500" />
                  <div>
                    <h2 className="text-xl font-bold">Configuracion de Chat</h2>
                    <p className="text-gray-500">Gestiona los chats y canales de comunicacion</p>
                  </div>
                </div>
                <div className="text-center py-12 text-gray-400">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Panel en desarrollo</p>
                </div>
              </div>
            </div>
          ) : currentView === 'server' ? (
            <ServerPanel user={user} />
          ) : currentView === 'agenda' ? (
            <AgendaPanel user={user} department={selectedDepartment} />
          ) : currentView === 'contactos' ? (
            <ContactosPanel user={user} />
          ) : currentView === 'account' ? (
            /* Panel Mi Cuenta - Seguridad y configuración */
            <div className="h-full p-6 overflow-y-auto">
              <div className="max-w-4xl mx-auto space-y-6">
                {/* Sección Avatar */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Camera className="w-5 h-5 text-[#3B82F6]" />
                    Avatar
                  </h2>
                  
                  {/* Avatar actual */}
                  <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                    {(() => {
                      const avatarUrl = user?.avatar_url;
                      if (avatarUrl && (avatarUrl.startsWith('data:') || avatarUrl.startsWith('http'))) {
                        return <img src={avatarUrl} alt="Avatar" className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-lg" />;
                      }
                      if (avatarUrl && avatarUrl.startsWith('avatar:')) {
                        const parts = avatarUrl.split(':');
                        const avatarId = parts[1];
                        const color = parts[2] || '#3B82F6';
                        return (
                          <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white shadow-lg">
                            <AvatarSVG avatarId={avatarId} color={color} size={64} />
                          </div>
                        );
                      }
                      return (
                        <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold border-2 border-white shadow-lg bg-[#3B82F6]">
                          {user?.name?.charAt(0) || 'U'}
                        </div>
                      );
                    })()}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{user?.name}</p>
                      <p className="text-sm text-gray-500">@{user?.username}</p>
                    </div>
                    <button 
                      onClick={() => setShowSettings(true)}
                      className="px-4 py-2 text-sm font-medium text-[#3B82F6] hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      Cambiar avatar
                    </button>
                  </div>
                </div>

                {/* Sección Perfil */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <UserCircle className="w-5 h-5 text-[#3B82F6]" />
                    Perfil
                  </h2>
                  {accountMessage && (
                    <div className={`mb-4 p-3 rounded-lg text-sm ${accountMessage.includes("Error") ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`}>
                      {accountMessage}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                      <input 
                        type="text" 
                        value={accountData.name}
                        onChange={(e) => setAccountData({...accountData, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input 
                        type="email" 
                        value={accountData.email}
                        onChange={(e) => setAccountData({...accountData, email: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent" 
                      />
                    </div>
                  </div>
                  <div className="flex justify-end mt-4 pt-4 border-t">
                    <button
                      onClick={handleSaveAccount}
                      disabled={savingAccount}
                      className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-lg hover:bg-[#2563EB] transition-colors disabled:opacity-50"
                    >
                      {savingAccount ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Guardar cambios
                    </button>
                  </div>
                </div>

                {/* Sección Seguridad */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-[#3B82F6]" />
                    Seguridad
                  </h2>
                  <div className="space-y-4">
                    {/* Cambiar contraseña */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Key className="w-5 h-5 text-gray-500" />
                        <div>
                          <p className="font-medium text-gray-900">Contraseña</p>
                          <p className="text-sm text-gray-500">Última actualización: hace 30 días</p>
                        </div>
                      </div>
                      <button className="px-4 py-2 text-sm font-medium text-[#3B82F6] hover:bg-blue-50 rounded-lg transition-colors">
                        Cambiar
                      </button>
                    </div>

                    {/* 2FA */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-gray-500" />
                        <div>
                          <p className="font-medium text-gray-900">Autenticación en dos pasos (2FA)</p>
                          <p className="text-sm text-gray-500">Añade una capa extra de seguridad</p>
                        </div>
                      </div>
                      <button className="px-4 py-2 text-sm font-medium bg-[#3B82F6] text-white hover:bg-[#2563EB] rounded-lg transition-colors">
                        Activar
                      </button>
                    </div>

                    {/* Caducidad contraseña */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-gray-500" />
                        <div>
                          <p className="font-medium text-gray-900">Caducidad de contraseña</p>
                          <p className="text-sm text-gray-500">Cada cuánto tiempo cambiar la contraseña</p>
                        </div>
                      </div>
                      <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#3B82F6]">
                        <option value="never">Nunca</option>
                        <option value="30">Cada 30 días</option>
                        <option value="60">Cada 60 días</option>
                        <option value="90">Cada 90 días</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Sección Integraciones */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Link2 className="w-5 h-5 text-[#3B82F6]" />
                    Integraciones
                  </h2>
                  <div className="space-y-4">
                    {/* Google Calendar */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-lg border border-gray-200 flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Google Calendar</p>
                          <p className="text-sm text-gray-500">Sincroniza tus tareas y trabajos</p>
                        </div>
                      </div>
                      <button className="px-4 py-2 text-sm font-medium border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-2">
                        <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4" />
                        Conectar
                      </button>
                    </div>

                    {/* Email */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-lg border border-gray-200 flex items-center justify-center">
                          <Mail className="w-5 h-5 text-red-500" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Notificaciones por Email</p>
                          <p className="text-sm text-gray-500">Recibe alertas importantes</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" defaultChecked className="sr-only peer" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#3B82F6]"></div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Botón Guardar */}
                <div className="flex justify-end">
                  <button className="px-6 py-2.5 bg-[#3B82F6] text-white font-medium rounded-lg hover:bg-[#2563EB] transition-colors">
                    Guardar cambios
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full p-4 flex flex-col">
              {selectedDepartment ? (
                <Chat department={selectedDepartment} user={user} />
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Selecciona un departamento para ver el chat</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* XIA Chat Bar - Fixed at bottom, oculta en vista Chat/AI */}
      {xiaBarVisible && currentView !== 'chat' && currentView !== 'ai' && (
        <div className={`fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg transition-all duration-300 z-40 ${
          isMobile ? 'left-0' : (sidebarOpen ? 'left-64' : 'left-20')
        }`}>
          {xiaBarExpanded ? (
            <div className={xiaBarHeights[xiaBarSize]}>
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-400">
                <div className="flex items-center gap-2">
                  <img src="/xia-icon.png" alt="XIA" className="w-6 h-6 object-contain" />
                  <span className="font-medium text-[#111827]">XIA</span>
                  {/* Botón Voz */}
                  <button
                    onClick={() => setXiaVoiceEnabled(!xiaVoiceEnabled)}
                    className={`p-1.5 rounded-lg transition-colors ${xiaVoiceEnabled ? 'bg-gray-200 text-gray-700' : 'hover:bg-gray-100 text-gray-400'}`}
                    title={xiaVoiceEnabled ? 'Voz activada' : 'Activar voz'}
                  >
                    {xiaVoiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  </button>
                  {/* Botón Micro */}
                  <button
                    onClick={toggleXiaListening}
                    className={`p-1.5 rounded-lg transition-colors ${xiaListening ? 'bg-red-100 text-red-600 animate-pulse' : 'hover:bg-gray-100 text-gray-400'}`}
                    title={xiaListening ? 'Escuchando...' : 'Hablar'}
                  >
                    {xiaListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  {/* Botones de tamaño */}
                  <button
                    onClick={() => setXiaBarSize(Math.max(0, xiaBarSize - 1))}
                    disabled={xiaBarSize === 0}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-30"
                    title="Más pequeño"
                  >
                    <ChevronDown className="w-4 h-4 text-[#6B7280]" />
                  </button>
                  <button
                    onClick={() => setXiaBarSize(Math.min(2, xiaBarSize + 1))}
                    disabled={xiaBarSize === 2}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-30"
                    title="Más grande"
                  >
                    <ChevronUp className="w-4 h-4 text-[#6B7280]" />
                  </button>
                  <button
                    onClick={() => setXiaBarExpanded(false)}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Minimizar"
                  >
                    <Minimize2 className="w-4 h-4 text-[#6B7280]" />
                  </button>
                  <button
                    onClick={() => setXiaBarVisible(false)}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Cerrar"
                  >
                    <X className="w-4 h-4 text-[#6B7280]" />
                  </button>
                </div>
              </div>
              <div className={`${xiaContentHeights[xiaBarSize]} overflow-y-auto p-4 bg-gray-50 space-y-3`}>
                {xiaMessages.map((msg, idx) => (
                  <div key={idx} className={`flex items-start gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`p-1 rounded-full ${msg.role === 'user' ? 'bg-[#6B7280]' : ''}`}>
                      {msg.role === 'user' ? (
                        <UserCircle className="w-5 h-5 text-white" />
                      ) : (
                        <img src="/xia-icon.png" alt="XIA" className="w-6 h-6 object-contain" />
                      )}
                    </div>
                    <div className={`rounded-2xl p-3 max-w-md shadow-sm ${
                      msg.role === 'user'
                        ? 'bg-[#3B82F6] text-white rounded-tr-none'
                        : 'bg-white text-[#111827] rounded-tl-none'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))}
                {xiaLoading && (
                  <div className="flex items-start gap-3">
                    <div className="p-1">
                      <img src="/xia-icon.png" alt="XIA" className="w-6 h-6 object-contain" />
                    </div>
                    <div className="bg-white rounded-2xl rounded-tl-none p-3 shadow-sm">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={xiaMessagesEndRef} />
              </div>
              <div className="p-3 border-t border-gray-400">
                {/* Vista previa de archivo adjunto */}
                {xiaFile && (
                  <div className="mb-2 p-2 bg-gray-50 rounded-lg flex items-center gap-2">
                    {xiaFilePreview ? (
                      <img src={xiaFilePreview} alt="Preview" className="w-12 h-12 object-cover rounded" />
                    ) : (
                      <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                        <FileIcon className="w-6 h-6 text-gray-500" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{xiaFile.name}</p>
                      <p className="text-xs text-gray-500">{(xiaFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button onClick={clearXiaFile} className="p-1 hover:bg-gray-200 rounded">
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {/* Input de archivo oculto */}
                  <input
                    ref={xiaFileInputRef}
                    type="file"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                    onChange={handleXiaFileSelect}
                    className="hidden"
                  />
                  {/* Botón de adjuntar */}
                  <button
                    onClick={() => xiaFileInputRef.current?.click()}
                    className="p-2.5 text-gray-500 hover:bg-gray-100 rounded-xl transition-colors"
                    title="Adjuntar archivo"
                  >
                    <Paperclip className="w-5 h-5" />
                  </button>
                  <input
                    type="text"
                    value={xiaMessage}
                    onChange={(e) => setXiaMessage(e.target.value)}
                    placeholder="Escribe tu mensaje..."
                    className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#3B82F6]"
                    onKeyPress={(e) => e.key === 'Enter' && handleXIASend()}
                    disabled={xiaLoading}
                  />
                  <button
                    id="xia-send-btn"
                    onClick={handleXIASend}
                    disabled={xiaLoading || (!xiaMessage.trim() && !xiaFile)}
                    className="p-2.5 bg-[#3B82F6] text-white rounded-xl hover:bg-[#2563EB] transition-colors disabled:opacity-50"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                  {/* Botón Modo Conversación */}
                  <button
                    onClick={toggleXiaConversationMode}
                    className={`relative p-2.5 rounded-xl transition-all duration-300 ${
                      xiaConversationMode
                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    title={xiaConversationMode ? 'Terminar conversación' : 'Modo conversación por voz'}
                    style={xiaConversationMode ? { animation: 'pulse-glow 2s ease-in-out infinite' } : {}}
                  >
                    {xiaConversationMode && xiaListening && (
                      <span className="absolute -top-1 -right-1 w-3 h-3">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                      </span>
                    )}
                    {xiaConversationMode ? (
                      xiaListening ? (
                        <Mic className="w-5 h-5 animate-pulse" />
                      ) : xiaSpeaking ? (
                        <Volume2 className="w-5 h-5 animate-pulse" />
                      ) : (
                        <PhoneOff className="w-5 h-5" />
                      )
                    ) : (
                      <Phone className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {/* CSS para animación */}
                <style>{`
                  @keyframes pulse-glow {
                    0%, 100% { box-shadow: 0 0 5px rgba(34, 197, 94, 0.5), 0 0 20px rgba(34, 197, 94, 0.3); }
                    50% { box-shadow: 0 0 20px rgba(34, 197, 94, 0.8), 0 0 40px rgba(34, 197, 94, 0.4); }
                  }
                `}</style>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 px-4 py-3">
              <button
                onClick={() => setXiaBarExpanded(true)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <img src="/xia-icon.png" alt="XIA" className="w-8 h-8 object-contain" />
              </button>
              <input
                type="text"
                value={xiaMessage}
                onChange={(e) => setXiaMessage(e.target.value)}
                placeholder="¿En que puedo ayudarte?"
                className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#3B82F6]"
                onFocus={() => setXiaBarExpanded(true)}
              />
              {/* Botón Conversación en barra minimizada */}
              <button
                onClick={toggleXiaConversationMode}
                className={`relative p-2 rounded-lg transition-all ${
                  xiaConversationMode
                    ? 'bg-green-500 text-white'
                    : 'hover:bg-gray-100 text-gray-500'
                }`}
                title={xiaConversationMode ? 'Terminar' : 'Hablar'}
              >
                {xiaConversationMode && xiaListening && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full animate-ping"></span>
                )}
                <Phone className="w-5 h-5" />
              </button>
              <button
                onClick={() => setXiaBarExpanded(true)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Maximize2 className="w-5 h-5 text-[#6B7280]" />
              </button>
              <button
                onClick={() => setXiaBarVisible(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Cerrar"
              >
                <X className="w-4 h-4 text-[#6B7280]" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Botón flotante para reabrir XIA si está cerrado */}
      {!xiaBarVisible && (
        <button
          onClick={() => setXiaBarVisible(true)}
          className={`fixed bottom-4 right-4 p-2 bg-white rounded-full shadow-lg hover:shadow-xl transition-all z-40`}
          title="Abrir XIA"
        >
          <img src="/xia-icon.png" alt="XIA" className="w-10 h-10 object-contain" />
        </button>
      )}
    </div>
  );
}
